/**
 * ENCRE : le disque n'est pas un bouton, c'est une surface d'eau. Des ondes s'y elargissent
 * lentement, l'une apres l'autre, et un reflet dort en haut a gauche.
 *
 * L'IDEE, EN UN MOT : une matiere et un geste. Rien ne represente rien — pas de pictogramme, pas
 * de bulle de dialogue — et c'est ce qui laisse la couleur du client porter tout le sens.
 *
 * AU TEMPS FORT, UNE GOUTTE TOMBE DE PLUS HAUT QUE LE LANCEUR et l'encre fleurit ou elle touche.
 * La goutte part donc d'au-dessus du disque : sa feuille de dessin monte a cent pixels pour un
 * lanceur de soixante-quatre, et c'est encore une raison pour laquelle un look ne peut pas vivre
 * dans le rond de la bulle, qui rogne ce qui depasse. Les ondes, elles, sont DANS l'eau : leur
 * conteneur les rogne, sinon elles sortiraient du disque en cercles parfaits.
 *
 * A L'OUVERTURE, l'eau se calme et deux traits d'encre se croisent a la surface, l'un puis
 * l'autre : la croix d'Encre est ecrite au trait, pas posee en barres.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'encre',

  css: `
/* L'EAU. Le degrade radial creuse la surface : plus clair au point ou la goutte tombera, plus
   sombre au bord. Le repli sert les navigateurs sans color-mix — ils ont une eau plate, pas une
   eau absente. */
.woot-lanceur[data-look="encre"] .woot-encre__disque {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  overflow: hidden;
  background: var(--client);
  background: radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--client) 55%, white) 0%, var(--client) 42%, color-mix(in srgb, var(--client) 70%, black) 100%);
  box-shadow: 0 12px 30px rgba(16, 19, 23, .28);
  box-shadow: 0 12px 30px color-mix(in srgb, var(--client) 38%, transparent);
  transition: filter .3s ease;
}

/* LES ONDES : trois cercles sur la meme boucle, decales d'un tiers, donc une onde toutes les
   secondes et demie sans jamais deux departs simultanes.

   LE RETARD VOYAGE DANS UNE VARIABLE, ET C'EST LA SEULE FACON QUE CA TIENNE. Ecrit dans une regle
   a part, il etait remis a zero par le raccourci de la propriete animation des regles d'etat, plus
   specifiques : les trois ondes partaient ensemble et le lanceur ne montrait qu'un anneau epais.
   La variable, elle, est lue au moment ou le raccourci s'applique. Meme mecanique que Braise. */
.woot-lanceur[data-look="encre"] .woot-encre__onde {
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, .75);
}

.woot-lanceur[data-look="encre"] .woot-encre__onde--deux { --woot-encre-retard: 1.55s; }
.woot-lanceur[data-look="encre"] .woot-encre__onde--trois { --woot-encre-retard: 3.1s; }

.woot-lanceur[data-look="encre"][data-etat="repos"] .woot-encre__onde,
.woot-lanceur[data-look="encre"][data-etat="attention"] .woot-encre__onde {
  animation: woot-encre-onde 4.6s ease-out var(--woot-encre-retard, 0s) infinite;
}

@keyframes woot-encre-onde {
  0% { transform: scale(.3); opacity: .85; }
  100% { transform: scale(1.5); opacity: 0; }
}

.woot-lanceur[data-look="encre"] .woot-encre__reflet {
  position: absolute;
  left: 12px;
  top: 10px;
  width: 22px;
  height: 11px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .6);
  filter: blur(2px);
}

/* La feuille de dessin monte au-dessus du lanceur : c'est de la que tombe la goutte. */
.woot-lanceur[data-look="encre"] .woot-encre__figure { position: absolute; inset: 0; }

.woot-lanceur[data-look="encre"] .woot-encre__figure svg {
  position: absolute;
  left: 0;
  bottom: 0;
  overflow: visible;
}

/* LE TEMPS FORT, EN DEUX TEMPS : la goutte tombe et s'ecrase (elle s'aplatit en fin de course),
   puis l'encre fleurit a l'endroit du choc, un quart de seconde apres l'impact. */
.woot-lanceur[data-look="encre"] .woot-encre__goutte {
  fill: var(--client);
  stroke: #ffffff;
  stroke-width: 1.2;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}

.woot-lanceur[data-look="encre"][data-etat="attention"] .woot-encre__goutte {
  animation: woot-encre-tombe 1.5s cubic-bezier(.45, 0, .85, .35);
}

@keyframes woot-encre-tombe {
  0% { opacity: 0; transform: translateY(0) scale(.6); }
  15% { opacity: 1; transform: translateY(6px) scale(1); }
  85% { opacity: 1; transform: translateY(58px) scale(1, 1.25); }
  100% { opacity: 0; transform: translateY(62px) scale(1.6, .3); }
}

/* La tache est floutee en bloc plutot que cercle par cercle : c'est la meme encre qui se diffuse,
   pas trois taches posees cote a cote. */
.woot-lanceur[data-look="encre"] .woot-encre__tache {
  opacity: 0;
  filter: blur(2.8px);
  transform-box: fill-box;
  transform-origin: center;
}

.woot-lanceur[data-look="encre"][data-etat="attention"] .woot-encre__tache {
  animation: woot-encre-fleurit 2.2s cubic-bezier(.2, .8, .2, 1) 1.25s;
}

@keyframes woot-encre-fleurit {
  0% { opacity: 0; transform: scale(.1); }
  25% { opacity: .85; }
  100% { opacity: 0; transform: scale(1.6); }
}

/* AU SURVOL la surface se tend : les ondes se pressent et l'eau s'eclaircit. Le selecteur porte
   [data-etat] pour peser autant que les regles d'etat au-dessus : sans ce poids, leur raccourci
   animation gagnerait et la duree du survol ne s'appliquerait jamais. */
.woot-lanceur[data-look="encre"][data-etat]:hover .woot-encre__onde { animation-duration: 2s; }
.woot-lanceur[data-look="encre"]:hover .woot-encre__disque { filter: brightness(1.08); }

/* OUVERT : l'eau se calme (les ondes cessent d'exister le temps de l'ouverture, sinon elles
   passeraient derriere la croix) et deux traits d'encre se croisent a la surface, l'un puis
   l'autre. Le trait est presque noir : c'est de l'encre sur l'eau du client, pas une barre
   blanche posee dessus. */
.woot-lanceur[data-look="encre"] .woot-encre__croix {
  fill: none;
  stroke: #0b0f19;
  stroke-width: 3;
  stroke-linecap: round;
  opacity: .85;
  stroke-dasharray: 44;
  stroke-dashoffset: 44;
  transition: stroke-dashoffset .55s cubic-bezier(.4, 0, .2, 1);
}

.woot-lanceur[data-look="encre"] .woot-encre__traits { filter: blur(.5px); }

.woot-lanceur[data-look="encre"][data-etat="ouvert"] .woot-encre__croix--un {
  stroke-dashoffset: 0;
  transition-delay: .15s;
}

.woot-lanceur[data-look="encre"][data-etat="ouvert"] .woot-encre__croix--deux {
  stroke-dashoffset: 0;
  transition-delay: .5s;
}

.woot-lanceur[data-look="encre"][data-etat="ouvert"] .woot-encre__onde { visibility: hidden; }
`,

  monter: conteneur => {
    const disque = document.createElement('div');
    disque.className = 'woot-encre__disque';
    ['', ' woot-encre__onde--deux', ' woot-encre__onde--trois'].forEach(
      rang => {
        const onde = document.createElement('span');
        onde.className = 'woot-encre__onde' + rang;
        disque.appendChild(onde);
      }
    );
    const reflet = document.createElement('span');
    reflet.className = 'woot-encre__reflet';
    disque.appendChild(reflet);

    const figure = document.createElement('div');
    figure.className = 'woot-encre__figure';
    figure.innerHTML =
      '<svg viewBox="0 0 64 100" width="64" height="100" aria-hidden="true">' +
      '<g class="woot-encre__tache" fill="#0b0f19">' +
      '<circle cx="32" cy="68" r="12" opacity=".6"/>' +
      '<circle cx="26" cy="72" r="8" opacity=".5"/>' +
      '<circle cx="39" cy="63" r="7" opacity=".5"/>' +
      '</g>' +
      '<g class="woot-encre__traits">' +
      '<path class="woot-encre__croix woot-encre__croix--un" d="M21 57c4 3 11 10 22 22"/>' +
      '<path class="woot-encre__croix woot-encre__croix--deux" d="M43 57c-4 3-11 10-22 22"/>' +
      '</g>' +
      '<circle class="woot-encre__goutte" cx="32" cy="8" r="3.6"/>' +
      '</svg>';

    conteneur.appendChild(disque);
    conteneur.appendChild(figure);
  },

  // ENCRE EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous
  // les looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
